import { queryOptions } from '@tanstack/react-query';
import { chunk } from 'lodash';

import { aesmidas, orbis, orbisMiddleware } from '../api';
import {
    BondQuote,
    OptionChain,
    OptionChainDates,
    Quote,
    QuoteHistory,
    TimeRange,
} from '@orbis-systems/orbis-api-middleware';
import { BondQuantityType, PriceType } from '@orbis-systems/orbis-api-middleware';
import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { isMidasQuoteSource } from '@/utils';

import { formatMidasOptionChainDates } from './utils';
import { BondQuoteDetail, BondSearchResult, MidasFxRateQuote } from './types';

const MUTUAL_FUND_DETAILS_BATCH_SIZE = 100;

export type QuoteEquityQueryParams = {
    symbols: string;
};

export type QuoteIntradayQueryParams = {
    symbol: string;
};

export type QuoteHistoricalQueryParams = {
    symbol: string;
    range?: TimeRange;
};

export type QuoteSearchQueryParams = {
    criteria: string;
    limit?: number;
    disableMutualFunds?: boolean;
};

export type QuoteBondsSearchQueryParams = {
    criteria: string;
    limit?: number;
};

export type QuoteGetBondsQueryParams = {
    identifiers: string;
};

export type MutualFundAllowedItem = {
    symbol: string;
    cusip: string;
};

export type MutualFundQuote = Quote & { cusip: string };

export type QuoteMutualFundDetailsQueryParams = {
    symbols: string[];
};

export type QuoteMutualFundCheckQueryParams = {
    symbol: string;
};

export type QuoteMutualFundCheckQueryResponse = Record<string, boolean>;

export type QuoteGetOptionQueryParams = {
    symbols: string[];
    greeks?: boolean;
};

export type QuoteGetOptionHistoricalQueryParams = {
    symbol: string;
    range: TimeRange;
};

export type QuoteFxRateQueryParams = {
    symbols: string[];
};

export type QuoteBondsChartQueryParams = {
    isin: string;
    frequency: '1day' | '15min' | '1min';
    start: string; // YYYY-MM-DD
    end: string; // YYYY-MM-DD
};

export type TradingOptionChainQuotesQueryParams = { symbol: string; date: string };
export type TradingOptionChainDatesQueryParams = { symbol: string };

type BondsChartData = Array<{
    timestamp: string;
    price: number;
    yield_to_worst: number;
}>;
export type QuoteBondsChartQueryResponse = SuccessClientResponse<BondsChartData>;

export type QuoteEquityQueryResponse = Quote[];
export type QuoteIntradayQueryResponse = QuoteHistory[];
export type QuoteHistoricalQueryResponse = QuoteHistory[];
export type QuoteSearchQueryResponse = Quote[];
export type QuoteBondsSearchQueryResponse = BondSearchResult[];
export type QuoteGetBondsQueryResponse = BondQuoteDetail | null;
export type QuoteMutualFundDetailsQueryResponse = MutualFundQuote[];
export type QuoteGetOptionQueryResponse = Quote[];
export type QuoteGetOptionHistoricalQueryResponse = QuoteHistory[];
export type QuoteFxRateQueryResponse = Quote[];

export const quoteQueries = {
    all: () => queryOptions({ queryKey: ['quote'] }),
    equity: (params: QuoteEquityQueryParams) =>
        queryOptions<QuoteEquityQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'equity', params],
            queryFn: () => orbisMiddleware.quote.getQuotes(params.symbols.split(',')),
        }),
    intradayChartData: (params: QuoteIntradayQueryParams) =>
        queryOptions<QuoteIntradayQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'intradayChartData', params],
            queryFn: () => orbisMiddleware.stat.getQuoteIntraday(params.symbol),
        }),
    historical: (params: QuoteHistoricalQueryParams) =>
        queryOptions<QuoteHistoricalQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'historical', params],
            queryFn: () => orbisMiddleware.stat.getQuoteHistorical(params.symbol, params.range),
        }),
    search: (params: QuoteSearchQueryParams) =>
        queryOptions<QuoteSearchQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'search', params],
            queryFn: () => orbisMiddleware.quote.searchQuotes(params),
        }),
    bondsSearch: (params: QuoteBondsSearchQueryParams) =>
        queryOptions<QuoteBondsSearchQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'bondsSearch', params],
            queryFn: () => orbisMiddleware.quote.searchBondQuotes(params.criteria, params.limit),
        }),
    getBonds: (params: QuoteGetBondsQueryParams) =>
        queryOptions<QuoteGetBondsQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'getBonds', params],
            queryFn: () => orbisMiddleware.quote.getBondQuote(params.identifiers),
        }),
    mutualFundsAllowed: () =>
        queryOptions<MutualFundAllowedItem[]>({
            queryKey: [...quoteQueries.all().queryKey, 'mutualFundsAllowed'],
            queryFn: async () => (await orbis.orders.v2.mutualFunds.listMutualFunds()).data,
        }),
    mutualFundDetails: (params: QuoteMutualFundDetailsQueryParams) =>
        queryOptions<QuoteMutualFundDetailsQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'mutualFundDetails', params],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(params.symbols, MUTUAL_FUND_DETAILS_BATCH_SIZE).map((symbolList) =>
                        orbis.orders.v2.mutualFunds.mutualFundDetails(symbolList.join(',')),
                    ),
                );
                const results = responses.flatMap((response) => response.data);
                return results.filter((quote: MutualFundQuote) => !quote.notFound);
            },
        }),
    mutualFundCheck: (params: QuoteMutualFundCheckQueryParams) =>
        queryOptions<QuoteMutualFundCheckQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'mutualFundCheck', params],
            queryFn: async () => (await orbis.orders.v2.mutualFunds.checkMutualFund(params.symbol)).data,
        }),
    optionChainDates: (params: TradingOptionChainDatesQueryParams) =>
        queryOptions<OptionChainDates>({
            queryKey: [...quoteQueries.all().queryKey, 'optionChainDates', params],
            queryFn: async () => {
                if (isMidasQuoteSource()) {
                    const data = (await aesmidas.quote.getOptionChainDates({ symbol: params.symbol })).data;
                    return formatMidasOptionChainDates(data);
                } else return orbisMiddleware.quote.getQuoteOptionChainDates(params.symbol);
            },
        }),
    optionChainQuotes: (params: TradingOptionChainQuotesQueryParams) =>
        queryOptions<OptionChain>({
            queryFn: () => orbisMiddleware.quote.getQuoteOptionChainQuotes(params.symbol, params.date),
            queryKey: [...quoteQueries.all().queryKey, 'optionChainQuotes', params],
        }),
    getOption: (params: QuoteGetOptionQueryParams) =>
        queryOptions<QuoteGetOptionQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'getOption', params],
            queryFn: () => orbisMiddleware.quote.getOptionQuotes(params.symbols, params.greeks),
        }),
    getOptionHistorical: (params: QuoteGetOptionHistoricalQueryParams) =>
        queryOptions<QuoteGetOptionHistoricalQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'getOptionHistorical', params],
            queryFn: () => orbisMiddleware.stat.getOptionHistorical(params.symbol, params.range),
        }),
    fxRate: (params: QuoteFxRateQueryParams) =>
        queryOptions<QuoteFxRateQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'fxRate', params],
            queryFn: () => orbisMiddleware.quote.getQuotesFxRate(params.symbols),
        }),
    bondsChart: (params: QuoteBondsChartQueryParams) =>
        queryOptions<QuoteBondsChartQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'bondsChart', params],
            queryFn: () => aesmidas.getApiClient().get<BondsChartData>('quote/bonds-chart', { params }),
        }),
    getMidasEquityQuotes: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<Quote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasEquityQuotes', normalizedSymbols],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(normalizedSymbols, 50).map(async (symbolList) => {
                        return aesmidas.quotesReformat.getEquity({ symbols: symbolList.join(',') });
                    }),
                );
                return responses.flatMap((response) => response.data);
            },
        });
    },
    getMidasBondQuotes: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<BondQuote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasBondQuotes', normalizedSymbols],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(normalizedSymbols, 20).map(async (symbolList) => {
                        return aesmidas.quotesReformat.getBonds({
                            identifiers: symbolList.join(','),
                            bondQuantityType: BondQuantityType.Par,
                            bondPriceType: PriceType.Decimal,
                        });
                    }),
                );
                return responses.flatMap((response) => response.data);
            },
        });
    },
    getMidasOptionQuotes: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<Quote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasOptionQuotes', normalizedSymbols],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(normalizedSymbols, 50).map(async (symbolList) => {
                        return aesmidas.quotesReformat.getOption({ symbols: symbolList.join(','), greeks: false });
                    }),
                );
                return responses.flatMap((response) => response.data);
            },
        });
    },
    getMidasFxRates: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<MidasFxRateQuote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasFxRates', normalizedSymbols],
            queryFn: async () => {
                const response = await aesmidas.quotesReformat.getQuotesReformat({
                    symbols: normalizedSymbols.join(','),
                });
                return response.data;
            },
        });
    },
};
